import React, { createContext, useCallback, useContext, useState } from 'react';
import { useSearch } from './SearchContext';

const FilterContext = createContext(null);

const getInitialFilters = (params, narrowSearchParamsViewModel, searchResultViewModel) => {
    const initialFilters = {};
    const jobTypeValue = params.get(narrowSearchParamsViewModel.jobTypeParamKey);
    if (jobTypeValue) {
        initialFilters[narrowSearchParamsViewModel.jobTypeParamKey] = jobTypeValue;
    }
    const salaryTypeValue = params.get(narrowSearchParamsViewModel.salaryTypeParamKey);
    if (salaryTypeValue) {
        initialFilters[narrowSearchParamsViewModel.salaryTypeParamKey] = salaryTypeValue;
    }
    const salaryMinValue = params.get(narrowSearchParamsViewModel.salaryMinParamKey);
    if (salaryMinValue) {
        initialFilters[narrowSearchParamsViewModel.salaryMinParamKey] = salaryMinValue;
    }
    const salaryMaxValue = params.get(narrowSearchParamsViewModel.salaryMaxParamKey);
    if (salaryMaxValue) {
        initialFilters[narrowSearchParamsViewModel.salaryMaxParamKey] = salaryMaxValue;
    }
    const datePostedValue = params.get(narrowSearchParamsViewModel.datePostedParamKey);
    if (datePostedValue) {
        initialFilters[narrowSearchParamsViewModel.datePostedParamKey] = datePostedValue;
    }
    const distanceValue = params.get(narrowSearchParamsViewModel.distanceParamKey);
    if (distanceValue) {
        initialFilters[narrowSearchParamsViewModel.distanceParamKey] = distanceValue;
    } else if (searchResultViewModel.defaultDistance) {
        // パラメータがない場合デフォルト値を設定
        initialFilters[narrowSearchParamsViewModel.distanceParamKey] = searchResultViewModel.defaultDistance.toString();
    }
    const featureParam = params.get(narrowSearchParamsViewModel.featureParamKey);
    if (featureParam) {
        const featureValues = featureParam.split(',').map(v => v.trim());
        initialFilters[narrowSearchParamsViewModel.featureParamKey] = {};
        featureValues.forEach(value => {
            for (const [categoryName, validValues] of Object.entries(narrowSearchParamsViewModel.feature)) {
                if (validValues.includes(Number(value))) {
                    if (!initialFilters[narrowSearchParamsViewModel.featureParamKey][categoryName]) {
                        initialFilters[narrowSearchParamsViewModel.featureParamKey][categoryName] = [];
                    }
                    initialFilters[narrowSearchParamsViewModel.featureParamKey][categoryName].push(value);
                    break;
                }
            }
        });
    }

    return initialFilters;
};

export const FilterProvider = ({ children, narrowSearchParamsViewModel, searchResultViewModel }) => {
    const { handleSearch } = useSearch();

    const [activeFilters, setActiveFilters] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return getInitialFilters(params, narrowSearchParamsViewModel, searchResultViewModel);
    });

    /**
     * フィルターを更新する
     * @param {string|Object} paramKeyOrUpdates - 更新するフィルターのキー、または一括更新時のフィルターオブジェクト
     * @param {string|number|null} [value=null] - フィルターの新しい設定値
     * @param {string|null} [category=null] - Featureのカテゴリー名
     * @description
     * - 単一更新の場合：paramKeyOrUpdatesにフィルターのパラメータキー（文字列）を指定
     * - 一括更新の場合：paramKeyOrUpdatesに複数のフィルターキーと値のペアを持つオブジェクトを指定
     * - Feature更新の場合：categoryにFeatureのカテゴリー名を指定
     */
    const handleFilterChange = useCallback(
        async (
            paramKeyOrUpdates,
            value = null,
            category = null
        ) => {
            const newFilters = { ...activeFilters };

            // 一括更新かどうかを判定
            if (typeof paramKeyOrUpdates === 'object' && paramKeyOrUpdates !== null) {
                Object.entries(paramKeyOrUpdates).forEach(([paramKey, value]) => {
                    if (value === null) {
                        delete newFilters[paramKey];
                    } else {
                        newFilters[paramKey] = value;
                    }
                });
            }
            // Feature の更新かどうかを判定
            else if (category !== null) {
                const paramKey = paramKeyOrUpdates;
                const categoryMap = Object.entries(narrowSearchParamsViewModel.featureCategories).reduce((acc, [key, val]) => {
                    acc[val] = key;
                    return acc;
                }, {});
                const actualCategoryName = categoryMap[category] || category;

                if (value === null) {
                    // Feature の削除
                    if (newFilters[paramKey]) {
                        delete newFilters[paramKey][actualCategoryName];
                        if (Object.keys(newFilters[paramKey]).length === 0) {
                            delete newFilters[paramKey];
                        }
                    }
                } else {
                    // Feature の追加
                    if (!newFilters[paramKey]) {
                        newFilters[paramKey] = {};
                    }
                    newFilters[paramKey][actualCategoryName] = [value];
                }
            }
            // 単一更新の場合
            else {
                const paramKey = paramKeyOrUpdates;
                if (value === null) {
                    delete newFilters[paramKey];
                } else {
                    newFilters[paramKey] = value;
                }
            }

            setActiveFilters(newFilters);

            const searchParams = new URLSearchParams();

            Object.entries(newFilters).forEach(([key, value]) => {
                // Distanceはデフォルト値と異なる場合のみパラメータに追加
                if (key === narrowSearchParamsViewModel.distanceParamKey) {
                    if (value !== searchResultViewModel.defaultDistance.toString()) {
                        searchParams.append(key, value);
                    }
                    return;
                }

                if (key === narrowSearchParamsViewModel.featureParamKey) {
                    const allFeatureValues = Object.values(value)
                        .flat()
                        .filter(val => val != null);

                    if (allFeatureValues.length > 0) {
                        searchParams.set(key, allFeatureValues.join(','));
                    }
                } else {
                    searchParams.append(key, value);
                }
            });

            await handleSearch({
                keywordSearch: '',
                locationSearch: '',
                locationSuggest: '',
                searchParams,
            }, false);
        },
        [activeFilters, handleSearch, narrowSearchParamsViewModel, searchResultViewModel]
    );

    const getFilterValue = useCallback(
        (paramKey, category = null) => {
            if (category) {
                const categoryKey = Object.keys(narrowSearchParamsViewModel.featureCategories).find(
                    key => narrowSearchParamsViewModel.featureCategories[key] === category
                );

                return categoryKey
                    ? activeFilters[paramKey]?.[categoryKey] || null
                    : activeFilters[paramKey]?.[category] || null;
            }
            return activeFilters[paramKey] || null;
        },
        [activeFilters, narrowSearchParamsViewModel.featureCategories]
    );

    const isFilterActive = useCallback(
        (paramKey, category = null) => {
            const value = getFilterValue(paramKey, category);
            // Distance の場合、パラメータの有無に関わらずデフォルト値があれば active とする
            if (paramKey === narrowSearchParamsViewModel.distanceParamKey) {
                return Boolean(value) || Boolean(searchResultViewModel.getDistance());
            }
            return Boolean(value?.length);
        },
        [getFilterValue, narrowSearchParamsViewModel.distanceParamKey, searchResultViewModel]
    );

    return (
        <FilterContext.Provider
            value={{
                activeFilters,
                handleFilterChange,
                isFilterActive,
                getFilterValue,
                narrowSearchParamsViewModel,
            }}
        >
            {children}
        </FilterContext.Provider>
    );
};

export const useFilter = () => {
    return useContext(FilterContext);
};
